import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { notify } from '../utils/notifications';
import RubikooBrand from '../components/RubikooBrand';
import LegalLinks from '../components/LegalLinks';

export default function SignInScreen() {
  const navigate = useNavigate();
  const { signIn } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSignIn = async () => {
    if (!email.trim()) { notify.error('Please enter your email'); return; }
    if (!password) { notify.error('Please enter your password'); return; }

    setIsLoading(true);
    try {
      const { error } = await signIn(email.trim(), password);
      if (error) {
        if (error.message.toLowerCase().includes('invalid login') || error.message.toLowerCase().includes('invalid credentials')) {
          notify.error('Incorrect email or password');
        } else if (error.message.toLowerCase().includes('email not confirmed')) {
          notify.error('Please confirm your email first', 'Check your inbox for a confirmation link');
        } else {
          notify.error('Sign in failed', error.message);
        }
        return;
      }
      notify.success('Welcome back!');
      navigate('/event-selector');
    } catch (err) {
      notify.error('Connection error', 'Could not reach the server — check your internet or try again');
      console.error('Sign in error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="rubikoo-auth-page size-full flex flex-col overflow-y-auto transition-colors duration-300"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header Section */}
      <div className="flex flex-col items-center justify-center pt-8 pb-8 px-6">
        <RubikooBrand />
        <h1 className="mt-8 text-[32px] font-semibold tracking-tight text-center" style={{ color: 'var(--app-text)' }}>Sign in</h1>
        <p className="text-base text-center mt-2" style={{ color: 'var(--app-text-muted)' }}>Access your events with one account.</p>
      </div>

      {/* Form */}
      <form className="flex flex-col gap-4 px-6 flex-1" onSubmit={event => { event.preventDefault(); void handleSignIn(); }}>
        {/* Email */}
        <div className="flex flex-col gap-2">
          <label htmlFor="signin-email" className="text-base" style={{ color: 'var(--app-text)' }}>Email Address</label>
          <input
            id="signin-email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            disabled={isLoading}
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="rounded-lg px-4 text-base outline-none transition-colors w-full"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              color: 'var(--app-text)',
              height: '52px',
            }}
          />
        </div>

        {/* Password */}
        <div className="flex flex-col gap-2">
          <label htmlFor="signin-password" className="text-base" style={{ color: 'var(--app-text)' }}>Password</label>
          <div
            className="flex items-stretch rounded-lg transition-colors focus-within:ring-1"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              '--tw-ring-color': 'var(--app-accent-solid)',
            } as React.CSSProperties}
          >
            <input
              id="signin-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              disabled={isLoading}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="min-w-0 flex-1 bg-transparent px-4 text-base outline-none"
              style={{ color: 'var(--app-text)', height: '56px' }}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              disabled={isLoading}
              onClick={() => setShowPassword(!showPassword)}
              className="px-4 transition-opacity active:opacity-70"
              style={{ color: 'var(--app-text-subtle)' }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Sign In Button */}
        <div className="flex flex-col gap-4 pt-4">
          <button
            type="submit"
            aria-label={isLoading ? 'Signing in' : 'Sign in'}
            disabled={isLoading}
            className="h-14 rounded-xl text-white text-lg w-full flex items-center justify-center active:scale-95 transition-transform disabled:opacity-70"
            style={{
              backgroundColor: 'var(--app-accent-solid)',
              boxShadow: '0 10px 20px var(--app-accent-glow)',
            }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Sign In'
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="h-12 text-sm text-center w-full active:opacity-70 transition-opacity"
            style={{ color: 'var(--app-text-muted)' }}
          >
            Forgot password?
          </button>
        </div>
      </form>

      {/* Footer */}
      <div className="px-6 pt-4">
        <div className="flex flex-wrap items-center justify-center gap-1">
          <span className="text-base" style={{ color: 'var(--app-text-muted)' }}>Don't have an account?</span>
          <button
            onClick={() => navigate('/signup')}
            className="text-base active:opacity-70 transition-opacity"
            style={{ color: 'var(--app-accent)', textDecoration: 'underline', textDecorationColor: 'var(--app-accent-border)' }}
          >
            Sign up
          </button>
        </div>
        <LegalLinks />
      </div>
    </div>
  );
}
